/**
 * Centralized Terminal Commands Configuration
 * 
 * This file contains all terminal commands, their descriptions, routes,
 * and responses. Use this across all terminal components for consistency.
 * 
 * Command Structure:
 * - cmd: The command string
 * - description: Short description for help menu
 * - action: Type of action (navigate, clear, easter-egg, show-response)
 * - route: Target route for navigation (if action is 'navigate')
 * - requiresConfirmation: Whether to show Y/N prompt before action
 * - confirmationMessage: Message to show in confirmation prompt
 * - response: Output to display when command is executed
 */

export const terminalCommands = [
  {
    cmd: "ls",
    description: "List projects",
    action: "show-response",
    response: `total 56
drwxr-xr-x  7 developer  staff   224 Jun 26 2026 .
drwxr-xr-x 12 developer  staff   384 Feb  5 2026 ..
-rw-r--r--  1 developer  staff  4.2K personal-website/
-rw-r--r--  1 developer  staff  2.1K portfolio-redesign/
-rw-r--r--  1 developer  staff  8.5K cool-side-project/
-rw-r--r--  1 developer  staff  1.3K random-experiments/
-rwxr-xr-x  1 developer  staff  5.5K echo-studio/
-rwxr-xr-x  1 developer  staff  9.8K dj-studio/

💡 Tip: Try the command 'dj-studio' to launch the professional browser DJ console!`,
  },
  {
    cmd: "dj-studio",
    description: "Launch professional DJ Mixing Console",
    action: "navigate",
    route: "/dj-studio",
    requiresConfirmation: true,
    confirmationMessage: "Start the professional DJ mixing console?",
    response: "Spinning up vinyl turntables & engaging audio decoders...",
  },
  {
    cmd: "echo-studio",
    description: "Open Real-Time Voice Echo Studio",
    action: "navigate",
    route: "/echo-studio",
    requiresConfirmation: true,
    confirmationMessage: "Open the Real-Time Voice Echo Studio?",
    response: "Initializing Audio Context & opening studio...",
  },
  {
    cmd: "cat about.me",
    description: "View about info",
    action: "navigate",
    route: "/about-me",
    requiresConfirmation: true,
    confirmationMessage: "This will navigate you to the About page. Continue?",
    response: "Opening about.me file...",
  },
  {
    cmd: "cat about.json",
    description: "View about info (JSON format)",
    action: "show-response",
    response: `{
  "name": "Developer Noob",
  "role": "Full Stack Developer",
  "status": "Available for opportunities",
  "interests": ["Coding", "Coffee", "Debugging at 3 AM"],
  "superpower": "Turning coffee into code",
  "weakness": "Semicolons",
  "motto": "It works on my machine ¯\\_(ツ)_/¯"
}

💡 Use 'cat about.me' to visit the full about page`,
  },
  {
    cmd: "ps aux",
    description: "Show skills",
    action: "show-response",
    response: `USER       PID  %CPU %MEM COMMAND
developer  001  95.2  8.5 /usr/bin/javascript
developer  002  87.4  7.2 /usr/bin/react
developer  003  76.8  6.1 /usr/bin/node.js
developer  004  71.3  5.8 /usr/bin/python
developer  005  68.9  5.4 /usr/bin/git
developer  006  45.2  3.7 /usr/bin/problem-solving
developer  007  32.1  2.4 /usr/bin/coffee-consumption
developer  008   8.7  1.2 /usr/bin/sleep [defunct]

⚡ Skills are running at optimal performance!`,
  },
  {
    cmd: "tail -f blog.log",
    description: "Read articles",
    action: "navigate",
    route: "/blog",
    requiresConfirmation: true,
    confirmationMessage: "Navigate to blog section?",
    response: "Streaming blog posts...",
  },
  {
    cmd: "ping contact",
    description: "Get in touch",
    action: "navigate",
    route: "/contact-me",
    requiresConfirmation: true,
    confirmationMessage: "Open contact form?",
    response: "Pinging contact server...",
  },
  {
    cmd: "cd projects",
    description: "Navigate to projects",
    action: "navigate",
    route: "/projects",
    requiresConfirmation: true,
    confirmationMessage: "Navigate to projects directory?",
    response: "Changing directory to projects...",
  },
  {
    cmd: "whoami",
    description: "Display current user",
    action: "show-response",
    response: `developer@noob

Full Name: DeveloperNoob
Role: Full Stack Developer
Level: Perpetual Learner 🎓
Location: ~/home/keyboard
Status: Probably debugging something

"I'm not a noob, I'm just really good at finding new ways to be confused!"`,
  },
  {
    cmd: "pwd",
    description: "Print working directory",
    action: "show-response",
    response: "/home/noob/developer-noob\n\nYou are here → 📍",
  },
  {
    cmd: "date",
    description: "Display current date",
    action: "show-response",
    response: () => {
      const now = new Date();
      return `${now.toDateString()} ${now.toLocaleTimeString()}\n\n⏰ Time flies when you're debugging!`;
    },
  },
  {
    cmd: "help",
    description: "Show available commands",
    action: "show-response",
    response: "help", // Special flag to trigger help display
  },
  {
    cmd: "systemctl status",
    description: "Check system status",
    action: "navigate",
    route: "https://status.developernoob.com",
    external: true,
    requiresConfirmation: true,
    confirmationMessage: "This will open the system status page in a new tab. Continue?",
    response: "Opening status page...",
  },
  {
    cmd: "neofetch",
    description: "System information",
    action: "show-response",
    response: `                   -\`
                  .o+\`              developer@noob
                 \`ooo/              --------------
                \`+oooo:             OS: Portfolio Linux x86_64
               \`+oooooo:            Host: Personal Website v2.0
               -+oooooo+:           Kernel: React 18.2.0
             \`/:-:++oooo+:          Uptime: ${Math.floor(Math.random() * 365)} days
            \`/++++/+++++++:         Shell: zsh 5.8
           \`/++++++++++++++:        Terminal: Browser Console
          \`/+++ooooooooooooo/\`      CPU: JavaScript Engine
         ./ooosssso++osssssso+\`     GPU: CSS Animations
        .oossssso-\`\`\`\`/ossssss+\`    Memory: Unlimited Ideas
       -osssssso.      :ssssssso.   
      :osssssss/        osssso+++.  Stack: MERN
     /ossssssss/        +ssssooo/-  Languages: JS, Python, Java
   \`/ossssso+/:-        -:/+osssso+\` 
  \`+sso+:-\`                 \`.-/+oso:
 \`++:.                           \`-/+/
 .\`                                 \`/`,
  },
  {
    cmd: "fortune",
    description: "Random fortune cookie",
    action: "show-response",
    response: () => {
      const fortunes = [
        "Your code will compile on the first try today... just kidding! 😄",
        "A bug you create today will become a feature tomorrow.",
        "You will find the missing semicolon... eventually.",
        "Stack Overflow will have the answer you seek.",
        "Today's lucky number is 404 - but you'll find what you're looking for anyway!",
        "Your commit message will be clear and descriptive... said no developer ever.",
        "The force is strong with your terminal skills! 🌟",
        "Beware of premature optimization - it is the root of all evil.",
      ];
      return fortunes[Math.floor(Math.random() * fortunes.length)];
    },
  },
  {
    cmd: "clear",
    description: "Clear terminal",
    action: "clear",
  },
  {
    cmd: "sudo make-coffee",
    description: "Brew some motivation",
    action: "easter-egg",
    response: "☕ Coffee brewing...\n[████████████████████] 100%\nMotivation level: +50%\nCaffeine level: MAXIMUM\n\n*You feel a surge of productivity!*",
  },
  {
    cmd: "rm -rf imposter-syndrome",
    description: "Delete self-doubt",
    action: "easter-egg",
    response: `rm: cannot remove 'imposter-syndrome': Permission denied
imposter-syndrome is write-protected.

💡 Tip: You're doing better than you think!

Try: sudo believe-in-yourself`,
  },
  {
    cmd: 'git commit -m "fixed everything"',
    description: "Famous last words",
    action: "easter-egg",
    response: `fatal: "everything" is not a valid fix. Try being more specific.

Did you mean:
  - git commit -m "fixed the bug"
  - git commit -m "fixed styling issues"
  - git commit -m "maybe fixed it?"
  - git commit -m "please work this time"

Also, running test suite...
❌ 47 tests failed
✅ 1 test passed (it was the hello world test)`,
  },
  {
    cmd: "npm install confidence",
    description: "Package not found",
    action: "easter-egg",
    response: `npm ERR! 404 Not Found - GET https://registry.npmjs.org/confidence
npm ERR! 404
npm ERR! 404  'confidence' is not in the npm registry.
npm ERR! 404
npm ERR! 404 Did you mean: npm install --save-dev anxiety?
npm ERR! 404
npm ERR! A complete log of this run can be found in:
npm ERR!   ~/developers/hopes-and-dreams.log

💡 Suggested alternatives:
   - npm install imposter-syndrome@latest
   - npm install stack-overflow-dependency`,
  },
  {
    cmd: "sudo rm -rf /",
    description: "Don't try this at home",
    action: "easter-egg",
    response: `⚠️  WHOA THERE, COWBOY! ⚠️

This command would delete everything... but I'm not going to let you do that!

🛡️  Protection system activated!
❌  Dangerous command blocked!

Maybe you meant:
  - sudo rm -rf imposter-syndrome
  - sudo apt-get install common-sense

Stay safe out there! 😅`,
  },
  {
    cmd: "hack pentagon",
    description: "Hollywood hacker mode",
    action: "easter-egg",
    response: `Initiating hack sequence...
[▓▓▓▓▓▓▓▓░░░░░░░░░░░░] 35%

ACCESS DENIED

Just kidding! This isn't a 90s movie 🎬
Try something more realistic, like:
  - debugging a semicolon for 3 hours
  - explaining what you do to your family`,
  },
  {
    cmd: "sl",
    description: "Typo of 'ls'",
    action: "easter-egg",
    response: `    (  ) (@@) ( )  (@)  ()    @@    O     @     O     @
   (@@@)
(    )
 (@@@@)

  (   )
====        ________                ___________
_D _|  |_______/        \\__I_I_____===__|_________|
 |(_)---  |   H\\________/ |   |        =|___ ___|
 /     |  |   H  |  |     |   |         ||_| |_||
|      |  |   H  |__--------------------| [___] |
| ________|___H__/__|_____/[][]~\\_______|       |
|/ |   |-----------I_____I [][] []  D   |=======|__

Choo choo! 🚂 Did you mean 'ls'?`,
  },
];

/**
 * Easter egg commands - fun commands with custom responses
 */
export const easterEggCommands = [
  "sudo make-coffee",
  "rm -rf imposter-syndrome",
  'git commit -m "fixed everything"',
  "npm install confidence",
  "sudo rm -rf /",
  "hack pentagon",
  "sl",
];

/**
 * Get the response for a command
 * If the response is a function, execute it to get dynamic content
 * @param {object} command - The command object
 * @returns {string} The response text
 */
export const getCommandResponse = (command) => {
  if (!command) return null;
  
  const response = command.response;
  
  // If response is a function, execute it
  if (typeof response === 'function') {
    return response();
  }
  
  return response;
};

/**
 * Get help text listing all available commands
 * @returns {string} Formatted help text
 */
export const getHelpText = () => {
  const maxCmdLength = Math.max(...terminalCommands.map(c => c.cmd.length));
  
  return terminalCommands
    .map((c) => `  ${c.cmd.padEnd(maxCmdLength + 2)} - ${c.description}`)
    .join('\n');
};

/**
 * Find a command by its name
 * @param {string} commandName - The command to find
 * @returns {object|undefined} The command object or undefined
 */
export const findCommand = (commandName) => {
  return terminalCommands.find((c) => c.cmd === commandName);
};

/**
 * Filter commands based on input for suggestions
 * @param {string} input - The input to filter by
 * @param {number} limit - Maximum number of suggestions
 * @returns {array} Filtered commands
 */
export const getCommandSuggestions = (input, limit = 5) => {
  if (!input?.trim()) return [];
  
  return terminalCommands
    .filter((cmd) => cmd.cmd.toLowerCase().includes(input.toLowerCase()))
    .slice(0, limit);
};

/**
 * Check if a command is an easter egg
 * @param {string} command - The command to check
 * @returns {boolean}
 */
export const isEasterEgg = (command) => {
  return easterEggCommands.includes(command);
};

/**
 * Check if a command requires confirmation
 * @param {object} command - The command object
 * @returns {boolean}
 */
export const requiresConfirmation = (command) => {
  return command?.requiresConfirmation === true;
};

/**
 * Get confirmation message for a command
 * @param {object} command - The command object
 * @returns {string|null}
 */
export const getConfirmationMessage = (command) => {
  return command?.confirmationMessage || null;
};
